import React from 'react';
import { Document, Page, Text, View, StyleSheet, Link } from '@react-pdf/renderer';
import { 
  commonStyles, 
  formatDate, 
  formatPhoneNumber, 
  parseHtmlToText,
  renderBulletPoints,
  isValidSummary
} from '../Services/ReactPDFService.jsx';

// Executive Template Styles - Consistent Professional Design
// Font Size Hierarchy:
// - Name: 22pt (header)
// - Section Titles: 12pt (uppercase, bold)
// - Job Title / Degree: 11pt (bold)
// - Company / School: 10pt (italic, accent color)
// - Body Text / Description: 10pt (regular)
// - Date / Location: 9pt (secondary color)
// - Small Text: 8pt

const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: '#ffffff',
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 40,
    fontFamily: 'Helvetica',
  },

  // Header Styles
  headerContainer: {
    backgroundColor: '#2c3e50',
    marginHorizontal: -40,
    marginTop: -40,
    paddingHorizontal: 40,
    paddingVertical: 25,
    marginBottom: 20,
    borderBottom: '3px solid #3498db',
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  nameSection: {
    flex: 1,
  },
  name: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
    letterSpacing: 0,
    lineHeight: 1.2,
  },
  subtitle: {
    fontSize: 11,
    color: '#bdc3c7',
    marginBottom: 2,
    marginTop: 4,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  tagline: {
    fontSize: 9,
    color: '#95a5a6',
    fontStyle: 'italic',
    marginTop: 2,
  },
  contactGrid: {
    alignItems: 'flex-end',
    marginTop: 5,
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 3,
  },
  contactText: {
    fontSize: 9,
    color: '#ecf0f1',
    lineHeight: 1.4,
  },

  // Summary Styles
  summarySection: {
    backgroundColor: '#f8f9fa',
    paddingHorizontal: 15,
    paddingVertical: 12,
    marginHorizontal: -40,
    marginBottom: 16,
    borderLeft: '3px solid #3498db',
  },
  summaryTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#2c3e50',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 6,
  },
  summaryText: {
    fontSize: 10,
    lineHeight: 1.6,
    color: '#333333',
    textAlign: 'justify',
  },
  summaryContainer: {
    backgroundColor: '#f8f9fa',
    paddingHorizontal: 15,
    paddingVertical: 12,
    marginHorizontal: -40,
    marginBottom: 16,
    borderLeft: '3px solid #3498db',
  },

  // Section Styles
  sectionContainer: {
    marginBottom: 14,
  },
  section: {
    marginBottom: 14,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    paddingBottom: 4,
    borderBottomWidth: 2,
    borderBottomColor: '#3498db',
  },
  sectionHeaderNoBorder: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    paddingBottom: 4,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#2c3e50',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },

  // Experience Styles
  experienceItem: {
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: '1px solid #e9ecef',
  },
  experienceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
  },
  experienceLeft: {
    flex: 1,
    paddingRight: 15,
  },
  experienceRight: {
    alignItems: 'flex-end',
    minWidth: 100,
  },
  jobTitleLine: {
    flex: 1,
  },
  jobTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 2,
    lineHeight: 1.3,
  },
  positionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 2,
    lineHeight: 1.3,
  },
  companyInfo: {
    fontSize: 10,
    color: '#3498db',
    fontStyle: 'italic',
  },
  companyName: {
    fontSize: 10,
    color: '#3498db',
    fontStyle: 'italic',
  },
  dateLocationContainer: {
    alignItems: 'flex-end',
  },
  dateRange: {
    fontSize: 9,
    color: '#666666',
    textAlign: 'right',
  },
  dateText: {
    fontSize: 9,
    color: '#666666',
    textAlign: 'right',
  },
  location: {
    fontSize: 8,
    color: '#888888',
    textAlign: 'right',
    marginTop: 1,
  },
  locationText: {
    fontSize: 8,
    color: '#888888',
    textAlign: 'right',
    marginTop: 1,
  },

  // Achievement/Description Styles
  achievementsList: {
    marginTop: 4,
  },
  achievementItem: {
    marginBottom: 2,
  },
  bulletPoint: {
    fontSize: 10,
    marginBottom: 3,
    paddingLeft: 12,
    lineHeight: 1.5,
    color: '#333333',
  },
  bulletText: {
    fontSize: 10,
    lineHeight: 1.5,
    color: '#333333',
  },
  descriptionContainer: {
    marginTop: 2,
  },
  description: {
    fontSize: 10,
    lineHeight: 1.5,
    color: '#333333',
  },

  // Education Styles
  educationItem: {
    marginBottom: 8,
    paddingBottom: 6,
    borderBottom: '1px solid #e9ecef',
  },
  educationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 2,
  },
  educationLeft: {
    flex: 1,
    paddingRight: 15,
  },
  educationRight: {
    alignItems: 'flex-end',
    minWidth: 100,
  },
  degree: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 2,
    lineHeight: 1.3,
  },
  degreeText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 2,
    lineHeight: 1.3,
  },
  schoolInfo: {
    fontSize: 10,
    color: '#3498db',
    fontStyle: 'italic',
  },
  schoolText: {
    fontSize: 10,
    color: '#3498db',
    fontStyle: 'italic',
  },
  schoolLine: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  schoolLocation: {
    fontSize: 9,
    color: '#666666',
    marginLeft: 5,
  },
  educationDate: {
    fontSize: 9,
    color: '#666666',
    textAlign: 'right',
    marginBottom: 1,
  },
  educationGPA: {
    fontSize: 9,
    color: '#666666',
    textAlign: 'right',
  },
  gpaText: {
    fontSize: 9,
    color: '#666666',
    textAlign: 'right',
  },

  // Skills Styles
  skillsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    backgroundColor: '#f8f9fa',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
  },
  skillItem: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '50%',
    marginBottom: 4,
    paddingRight: 8,
  },
  skillBullet: {
    fontSize: 8,
    color: '#3498db',
    marginRight: 5,
    lineHeight: 1.4,
  },
  skillText: {
    fontSize: 10,
    lineHeight: 1.4,
    flex: 1,
    color: '#333333',
  },
  skillsContainer: {
    backgroundColor: '#f8f9fa',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 4,
  },
  skillCategory: {
    marginBottom: 3,
  },
  skillCategoryTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  skillList: {
    fontSize: 10,
    color: '#333333',
    lineHeight: 1.4,
  },
  skillsList: {
    fontSize: 10,
    color: '#333333',
    lineHeight: 1.5,
  },

  // Projects Styles
  projectsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  projectItem: {
    marginBottom: 8,
    paddingBottom: 6,
    borderBottom: '1px solid #e9ecef',
  },
  projectHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  projectTechContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 2,
    marginBottom: 3,
  },
  projectTechLabel: {
    fontSize: 9,
    color: '#666666',
    fontStyle: 'italic',
    marginRight: 4,
  },
  projectTechText: {
    fontSize: 9,
    color: '#666666',
    fontStyle: 'italic',
    flex: 1,
    lineHeight: 1.4,
  },
  projectTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 2,
    lineHeight: 1.3,
  },
  projectDescription: {
    fontSize: 10,
    lineHeight: 1.5,
    color: '#333333',
  },

  // Certification Styles
  certificationItem: {
    marginBottom: 6,
    paddingBottom: 4,
  },
  certificationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  certificationLeft: {
    flex: 1,
    paddingRight: 15,
  },
  certificationRight: {
    alignItems: 'flex-end',
    minWidth: 80,
  },
  certName: {
    fontSize: 11,
    fontWeight: 'normal',
    color: '#2c3e50',
    lineHeight: 1.3,
  },
  certAuthority: {
    fontSize: 10,
    color: '#3498db',
    fontStyle: 'italic',
  },
  certDate: {
    fontSize: 9,
    color: '#666666',
    textAlign: 'right',
  },
});

// Helper function to safely extract skill name from string or object
const getSkillName = (skill) => {
  if (typeof skill === "string") return skill;
  if (skill && typeof skill === "object") return skill.name || skill.skill || "";
  return "";
};

// Helper function to join skills array (handles both strings and objects)
const joinSkills = (skillsArray) => {
  if (!Array.isArray(skillsArray)) return "";
  return skillsArray.map(getSkillName).filter(Boolean).join(", ");
};

// Helper to get language name from string or object
const getLanguageName = (lang) => {
  if (typeof lang === "string") return lang;
  if (lang && typeof lang === "object") return lang.name || lang.language || "";
  return "";
};

// Helper to get interest name from string or object
const getInterestName = (interest) => {
  if (typeof interest === "string") return interest;
  if (interest && typeof interest === "object") return interest.name || interest.title || "";
  return "";
};

const ExecutiveTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    summary = '',
    professionalTitle = '',
    customSections = [],
    sectionTitles = {},
    // New sections
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
  } = resumeData || {};

  // Default section titles
  const defaultTitles = {
    summary: 'Executive Summary',
    experience: 'Professional Experience',
    education: 'Education',
    skills: 'Core Competencies',
    projects: 'Key Projects',
    certifications: 'Certifications',
    achievements: 'Achievements',
    languages: 'Languages',
    awards: 'Awards & Recognition',
    // New sections
    volunteering: 'Volunteering',
    interests: 'Interests',
    industryExpertise: 'Industry Expertise',
    publications: 'Publications',
    references: 'References',
  };

  // Get section title with fallback to default
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Create dynamic styles based on customizations
  const createDynamicStyles = (baseStyles, customizations) => {
    // Only apply customizations if they exist, otherwise use original styles
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;

    if (!hasCustomizations) {
      return baseStyles; // Return original styles unchanged
    }

    const {
      fontSize,
      sectionSpacing,
      titleColor,
      subtitleColor,
      fontFamily
    } = customizations;

    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    // Font size scaling logic:
    // The slider passes values 10-20, with default 14
    // We scale relative to the default (14) to adjust all fonts proportionally
    // Scale factor: fontSize / 14 (e.g., 10/14 = 0.71, 14/14 = 1.0, 20/14 = 1.43)
    if (fontSize) {
      const scaleFactor = fontSize / 14;

      // Apply scale factor to base template sizes
      // Base sizes: Name: 22pt, Section: 12pt, Job/Degree: 11pt, Body: 10pt, Date: 9pt, Small: 8pt
      customStyleOverrides.name = {
        ...baseStyles.name,
        fontSize: Math.round(22 * scaleFactor),
        letterSpacing: 0,
      };
      customStyleOverrides.subtitle = {
        ...baseStyles.subtitle,
        fontSize: Math.round(11 * scaleFactor),
      };
      customStyleOverrides.contactText = {
        ...baseStyles.contactText,
        fontSize: Math.round(9 * scaleFactor),
      };
      customStyleOverrides.sectionTitle = {
        ...baseStyles.sectionTitle,
        fontSize: Math.round(12 * scaleFactor),
      };
      customStyleOverrides.summaryText = {
        ...baseStyles.summaryText,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.positionTitle = {
        ...baseStyles.positionTitle,
        fontSize: Math.round(11 * scaleFactor),
      };
      customStyleOverrides.jobTitle = {
        ...baseStyles.jobTitle,
        fontSize: Math.round(11 * scaleFactor),
      };
      customStyleOverrides.companyName = {
        ...baseStyles.companyName,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.companyInfo = {
        ...baseStyles.companyInfo,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.dateText = {
        ...baseStyles.dateText,
        fontSize: Math.round(9 * scaleFactor),
      };
      customStyleOverrides.dateRange = {
        ...baseStyles.dateRange,
        fontSize: Math.round(9 * scaleFactor),
      };
      customStyleOverrides.locationText = {
        ...baseStyles.locationText,
        fontSize: Math.round(8 * scaleFactor),
      };
      customStyleOverrides.location = {
        ...baseStyles.location,
        fontSize: Math.round(8 * scaleFactor),
      };
      customStyleOverrides.degreeText = {
        ...baseStyles.degreeText,
        fontSize: Math.round(11 * scaleFactor),
      };
      customStyleOverrides.degree = {
        ...baseStyles.degree,
        fontSize: Math.round(11 * scaleFactor),
      };
      customStyleOverrides.schoolText = {
        ...baseStyles.schoolText,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.schoolInfo = {
        ...baseStyles.schoolInfo,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.gpaText = {
        ...baseStyles.gpaText,
        fontSize: Math.round(9 * scaleFactor),
      };
      customStyleOverrides.educationDate = {
        ...baseStyles.educationDate,
        fontSize: Math.round(9 * scaleFactor),
      };
      customStyleOverrides.skillCategoryTitle = {
        ...baseStyles.skillCategoryTitle,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.skillsList = {
        ...baseStyles.skillsList,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.skillText = {
        ...baseStyles.skillText,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.projectTitle = {
        ...baseStyles.projectTitle,
        fontSize: Math.round(11 * scaleFactor),
      };
      customStyleOverrides.projectTechText = {
        ...baseStyles.projectTechText,
        fontSize: Math.round(9 * scaleFactor),
      };
      customStyleOverrides.projectTechLabel = {
        ...baseStyles.projectTechLabel,
        fontSize: Math.round(9 * scaleFactor),
      };
      customStyleOverrides.certName = {
        ...baseStyles.certName,
        fontSize: Math.round(11 * scaleFactor),
      };
      customStyleOverrides.certAuthority = {
        ...baseStyles.certAuthority,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.certDate = {
        ...baseStyles.certDate,
        fontSize: Math.round(9 * scaleFactor),
      };
      customStyleOverrides.description = {
        ...baseStyles.description,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.bulletText = {
        ...baseStyles.bulletText,
        fontSize: Math.round(10 * scaleFactor),
      };
      customStyleOverrides.bulletPoint = {
        ...baseStyles.bulletPoint,
        fontSize: Math.round(10 * scaleFactor),
      };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionTitle = {
        ...customStyleOverrides.sectionTitle || baseStyles.sectionTitle,
        marginTop: sectionSpacing,
        marginBottom: sectionSpacing * 0.5,
      };
      customStyleOverrides.section = {
        ...baseStyles.section,
        marginBottom: sectionSpacing,
      };
      customStyleOverrides.summaryContainer = {
        ...baseStyles.summaryContainer,
        marginBottom: sectionSpacing,
      };
      customStyleOverrides.experienceHeader = {
        ...baseStyles.experienceHeader,
        marginBottom: sectionSpacing * 0.3,
      };
    }

    if (titleColor) {
      // Note: Executive template header has dark background, so name stays white
      // Only apply title color to section titles
      customStyleOverrides.sectionTitle = {
        ...customStyleOverrides.sectionTitle || baseStyles.sectionTitle,
        color: titleColor,
      };
    }

    if (subtitleColor) {
      customStyleOverrides.positionTitle = {
        ...customStyleOverrides.positionTitle || baseStyles.positionTitle,
        color: subtitleColor,
      };
      customStyleOverrides.companyName = {
        ...customStyleOverrides.companyName || baseStyles.companyName,
        color: subtitleColor,
      };
      customStyleOverrides.degreeText = {
        ...customStyleOverrides.degreeText || baseStyles.degreeText,
        color: subtitleColor,
      };
      customStyleOverrides.schoolText = {
        ...customStyleOverrides.schoolText || baseStyles.schoolText,
        color: subtitleColor,
      };
      customStyleOverrides.skillCategoryTitle = {
        ...customStyleOverrides.skillCategoryTitle || baseStyles.skillCategoryTitle,
        color: subtitleColor,
      };
      customStyleOverrides.projectTitle = {
        ...customStyleOverrides.projectTitle || baseStyles.projectTitle,
        color: subtitleColor,
      };
      customStyleOverrides.certName = {
        ...customStyleOverrides.certName || baseStyles.certName,
        color: subtitleColor,
      };
      customStyleOverrides.certAuthority = {
        ...customStyleOverrides.certAuthority || baseStyles.certAuthority,
        color: subtitleColor,
      };
    }

    return StyleSheet.create({
      ...baseStyles,
      ...customStyleOverrides,
    });
  };

  // Merge base styles with custom overrides
  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Define section order and content
  const defaultSectionOrder = [
    'summary',
    'workExperience',
    'education',
    'skills',
    'projects',
    'certifications',
    'achievements',
    'languages',
    'awards',
    'volunteering',
    'interests',
    'industryExpertise',
    'publications',
    'references'
  ];

  // Build final section order - include custom sections that aren't in the order
  const customSectionIds = customSections
    .filter(cs => cs.id && cs.title && cs.items?.length > 0)
    .map(cs => cs.id);

  const baseSectionOrder = customSectionOrder || defaultSectionOrder;

  // Add custom section IDs that aren't already in the order
  const finalSectionOrder = [
    ...baseSectionOrder,
    ...customSectionIds.filter(id => !baseSectionOrder.includes(id))
  ];

  // Define section components
  const sectionComponents = {
    summary: () => summary && isValidSummary(summary) && (
      <View style={mergedStyles.summaryContainer} wrap={false}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('summary')}</Text>
        <Text style={mergedStyles.summaryText}>
          {parseHtmlToText(summary)}
        </Text>
      </View>
    ),

    workExperience: () => workExperience.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('experience')}</Text>
        {workExperience.map((job, index) => (
          <View key={index} style={mergedStyles.experienceItem} wrap={false}>
            <View style={mergedStyles.experienceHeader}>
              <View style={mergedStyles.experienceLeft}>
                <Text style={mergedStyles.positionTitle}>{job.jobTitle}</Text>
                <Text style={mergedStyles.companyName}>{job.company}</Text>
              </View>
              <View style={mergedStyles.experienceRight}>
                <Text style={mergedStyles.dateText}>
                  {formatDate(job.startDate)} - {(job.isCurrentJob || job.endDate === 'Present' || job.endDate === 'Current' || !job.endDate) ? 'Present' : formatDate(job.endDate)}
                </Text>
                {job.location && (
                  <Text style={mergedStyles.locationText}>{job.location}</Text>
                )}
              </View>
            </View>
            {job.description && (
              <View style={mergedStyles.descriptionContainer}>
                {renderBulletPoints(parseHtmlToText(job.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    education: () => education.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('education')}</Text>
        {education.map((edu, index) => (
          <View key={index} style={mergedStyles.educationItem} wrap={false}>
            <View style={mergedStyles.educationHeader}>
              <View style={mergedStyles.educationLeft}>
                <Text style={mergedStyles.degreeText}>
                  {edu.degree} {edu.fieldOfStudy && `in ${edu.fieldOfStudy}`}
                </Text>
                <Text style={mergedStyles.schoolText}>
                  {edu.school}{edu.location && `, ${edu.location}`}
                </Text>
              </View>
              <View style={mergedStyles.educationRight}>
                <Text style={mergedStyles.dateText}>
                  {formatDate(edu.startDate)} - {formatDate(edu.endDate)}
                </Text>
                {edu.gpa && (
                  <Text style={mergedStyles.gpaText}>GPA: {edu.gpa}</Text>
                )}
              </View>
            </View>
          </View>
        ))}
      </View>
    ),

    skills: () => (skills.technical?.length > 0 || skills.soft?.length > 0 || skills.tools?.length > 0 || skills.other?.length > 0 || skills.languages?.length > 0 || skills.frameworks?.length > 0 || skills.developer_tools?.length > 0 || skills.list?.length > 0) && (
      <View style={mergedStyles.section} wrap={false}>
        <Text style={mergedStyles.sectionTitle}>{getSectionTitle('skills')}</Text>
        {/* Check if we have categorized skills */}
        {(skills.technical?.length > 0 || skills.soft?.length > 0 || skills.tools?.length > 0 || skills.other?.length > 0 || skills.languages?.length > 0 || skills.frameworks?.length > 0 || skills.developer_tools?.length > 0) ? (
          <View>
            {skills.technical?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillsList}>
                  <Text style={mergedStyles.skillCategoryTitle}>Technical Skills: </Text>
                  {joinSkills(skills.technical)}
                </Text>
              </View>
            )}
            {skills.soft?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillsList}>
                  <Text style={mergedStyles.skillCategoryTitle}>Soft Skills: </Text>
                  {joinSkills(skills.soft)}
                </Text>
              </View>
            )}
            {skills.tools?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillsList}>
                  <Text style={mergedStyles.skillCategoryTitle}>Tools: </Text>
                  {joinSkills(skills.tools)}
                </Text>
              </View>
            )}
            {skills.languages?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillsList}>
                  <Text style={mergedStyles.skillCategoryTitle}>Languages: </Text>
                  {joinSkills(skills.languages)}
                </Text>
              </View>
            )}
            {skills.frameworks?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillsList}>
                  <Text style={mergedStyles.skillCategoryTitle}>Frameworks: </Text>
                  {joinSkills(skills.frameworks)}
                </Text>
              </View>
            )}
            {skills.developer_tools?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillsList}>
                  <Text style={mergedStyles.skillCategoryTitle}>Developer Tools: </Text>
                  {joinSkills(skills.developer_tools)}
                </Text>
              </View>
            )}
            {skills.other?.length > 0 && (
              <View style={mergedStyles.skillCategory}>
                <Text style={mergedStyles.skillsList}>
                  <Text style={mergedStyles.skillCategoryTitle}>Other: </Text>
                  {joinSkills(skills.other)}
                </Text>
              </View>
            )}
          </View>
        ) : (
          // Fallback to flat list if no categorized skills
          <View style={mergedStyles.skillsGrid}>
            <Text style={mergedStyles.skillsList}>
              {skills.list?.map(getSkillName).filter(Boolean).join(' • ') || ''}
            </Text>
          </View>
        )}
      </View>
    ),

    projects: () => projects.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('projects')}</Text>
        {projects.map((project, index) => (
          <View key={index} style={mergedStyles.projectItem} wrap={false}>
            <View style={mergedStyles.projectHeader}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {(project.url || project.link) && (
                <Link
                  src={(project.url || project.link).startsWith('http') ?
                    (project.url || project.link) :
                    `https://${project.url || project.link}`
                  }
                  style={{
                    fontSize: mergedStyles.projectTechText.fontSize,
                    color: '#2b6cb0',
                    marginLeft: 4,
                    textDecoration: 'underline',
                    fontWeight: 'bold'
                  }}
                >
                  [LINK]
                </Link>
              )}
            </View>
            {project.technologies && (
              <View style={mergedStyles.projectTechContainer}>
                <Text style={mergedStyles.projectTechLabel}>Technologies:</Text>
                <Text style={mergedStyles.projectTechText}>{project.technologies}</Text>
              </View>
            )}
            {project.description && (
              <View style={mergedStyles.descriptionContainer}>
                {renderBulletPoints(parseHtmlToText(project.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    certifications: () => certifications.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('certifications')}</Text>
        {certifications.map((cert, index) => (
          <View key={index} style={{ flexDirection: 'row', marginBottom: 4 }} wrap={false}>
            <Text style={{ fontSize: 10, color: '#333333', marginRight: 6, lineHeight: 1.5 }}>•</Text>
            <Text style={{ fontSize: 10, color: '#333333', lineHeight: 1.5, flex: 1 }}>
              {cert.name}
              {(cert.authority || cert.issuer) && ` - ${cert.authority || cert.issuer}`}
              {cert.date && ` (${formatDate(cert.date)})`}
            </Text>
          </View>
        ))}
      </View>
    ),

    achievements: () => achievements.length > 0 && (
      <View style={mergedStyles.section}>
        <Text style={mergedStyles.sectionTitle} minPresenceAhead={50}>{getSectionTitle('achievements')}</Text>
        <View style={mergedStyles.achievementsList}>
          {achievements.map((achievement, index) => (
            <View key={index} style={mergedStyles.achievementItem} wrap={false}>
              {achievement.description && (
                <View style={mergedStyles.descriptionContainer}>
                  {renderBulletPoints(achievement.description, mergedStyles)}
                </View>
              )}
            </View>
          ))}
        </View>
      </View>
    ),

    languages: () => languages.length > 0 && (
      <View style={mergedStyles.section} wrap={false}>
        <View style={mergedStyles.sectionHeaderNoBorder}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('languages')}</Text>
        </View>
        <View style={mergedStyles.skillsGrid}>
          {languages.map((lang, index) => (
            <View key={index} style={mergedStyles.skillItem}>
              <Text style={mergedStyles.skillBullet}>•</Text>
              <Text style={mergedStyles.skillText}>
                {getLanguageName(lang)}{lang.proficiency ? ` - ${lang.proficiency}` : ''}
              </Text>
            </View>
          ))}
        </View>
      </View>
    ),

    awards: () => awards.length > 0 && (
      <View style={mergedStyles.section}>
        <View style={mergedStyles.sectionHeaderNoBorder} minPresenceAhead={50}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('awards')}</Text>
        </View>
        {awards.map((award, index) => (
          <View key={index} style={mergedStyles.certificationItem} wrap={false}>
            <View style={mergedStyles.certificationHeader}>
              <View style={mergedStyles.certificationLeft}>
                <Text style={mergedStyles.certName}>{award.title}</Text>
                {award.issuer && (
                  <Text style={mergedStyles.certAuthority}>{award.issuer}</Text>
                )}
              </View>
              <View style={mergedStyles.certificationRight}>
                {award.date && (
                  <Text style={mergedStyles.dateText}>
                    {formatDate(award.date)}
                  </Text>
                )}
              </View>
            </View>
            {award.description && (
              <View style={mergedStyles.descriptionContainer}>
                {renderBulletPoints(parseHtmlToText(award.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    // Volunteering section
    volunteering: () => volunteering.length > 0 && (
      <View style={mergedStyles.section}>
        <View style={mergedStyles.sectionHeaderNoBorder} minPresenceAhead={50}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('volunteering')}</Text>
        </View>
        {volunteering.map((vol, index) => (
          <View key={index} style={mergedStyles.experienceItem} wrap={false}>
            <View style={mergedStyles.experienceHeader}>
              <View style={mergedStyles.experienceLeft}>
                <Text style={mergedStyles.positionTitle}>{vol.role}</Text>
                <Text style={mergedStyles.companyName}>{vol.organization}</Text>
              </View>
              <View style={mergedStyles.experienceRight}>
                {(vol.startDate || vol.endDate) && (
                  <Text style={mergedStyles.dateText}>
                    {formatDate(vol.startDate)} - {vol.isCurrentRole ? 'Present' : formatDate(vol.endDate)}
                  </Text>
                )}
                {vol.location && (
                  <Text style={mergedStyles.locationText}>{vol.location}</Text>
                )}
              </View>
            </View>
            {vol.description && (
              <View style={mergedStyles.descriptionContainer}>
                {renderBulletPoints(parseHtmlToText(vol.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    // Interests section
    interests: () => interests.length > 0 && (
      <View style={mergedStyles.section} wrap={false}>
        <View style={mergedStyles.sectionHeaderNoBorder}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('interests')}</Text>
        </View>
        <View style={mergedStyles.skillsGrid}>
          {interests.map((interest, index) => (
            <View key={index} style={mergedStyles.skillItem}>
              <Text style={mergedStyles.skillBullet}>•</Text>
              <Text style={mergedStyles.skillText}>{getInterestName(interest)}</Text>
            </View>
          ))}
        </View>
      </View>
    ),

    // Industry Expertise section
    industryExpertise: () => industryExpertise.length > 0 && (
      <View style={mergedStyles.section}>
        <View style={mergedStyles.sectionHeaderNoBorder} minPresenceAhead={50}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('industryExpertise')}</Text>
        </View>
        {industryExpertise.map((exp, index) => (
          <View key={index} style={mergedStyles.certificationItem} wrap={false}>
            <View style={mergedStyles.certificationHeader}>
              <View style={mergedStyles.certificationLeft}>
                <Text style={mergedStyles.certName}>{exp.name}</Text>
                {exp.proficiency && (
                  <Text style={mergedStyles.certAuthority}>{exp.proficiency}</Text>
                )}
              </View>
              <View style={mergedStyles.certificationRight}>
                {exp.yearsOfExperience && (
                  <Text style={mergedStyles.dateText}>{exp.yearsOfExperience} years</Text>
                )}
              </View>
            </View>
          </View>
        ))}
      </View>
    ),

    // Publications section
    publications: () => publications.length > 0 && (
      <View style={mergedStyles.section}>
        <View style={mergedStyles.sectionHeaderNoBorder} minPresenceAhead={50}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('publications')}</Text>
        </View>
        {publications.map((pub, index) => (
          <View key={index} style={mergedStyles.certificationItem} wrap={false}>
            <View style={mergedStyles.certificationHeader}>
              <View style={mergedStyles.certificationLeft}>
                <Text style={mergedStyles.certName}>{pub.title}</Text>
                {pub.journal && (
                  <Text style={mergedStyles.certAuthority}>{pub.journal}</Text>
                )}
              </View>
              <View style={mergedStyles.certificationRight}>
                {pub.date && (
                  <Text style={mergedStyles.dateText}>{formatDate(pub.date)}</Text>
                )}
              </View>
            </View>
            {pub.authors && pub.authors.length > 0 && (
              <Text style={mergedStyles.certAuthority}>{pub.authors.join(', ')}</Text>
            )}
            {pub.description && (
              <View style={mergedStyles.descriptionContainer}>
                {renderBulletPoints(parseHtmlToText(pub.description), mergedStyles)}
              </View>
            )}
          </View>
        ))}
      </View>
    ),

    // References section
    references: () => references.length > 0 && (
      <View style={mergedStyles.section}>
        <View style={mergedStyles.sectionHeaderNoBorder} minPresenceAhead={50}>
          <Text style={mergedStyles.sectionTitle}>{getSectionTitle('references')}</Text>
        </View>
        {references.map((ref, index) => (
          <View key={index} style={mergedStyles.certificationItem} wrap={false}>
            <Text style={mergedStyles.certName}>{ref.name}</Text>
            {(ref.title || ref.company) && (
              <Text style={mergedStyles.certAuthority}>
                {ref.title}{ref.title && ref.company ? ' at ' : ''}{ref.company}
              </Text>
            )}
            {(ref.email || ref.phone) && (
              <Text style={mergedStyles.certDate}>
                {[ref.email, ref.phone].filter(Boolean).join(' • ')}
              </Text>
            )}
          </View>
        ))}
      </View>
    ),

    // Dynamic custom sections - each custom section gets its own render function
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View style={mergedStyles.section} wrap={false}>
            <View style={mergedStyles.sectionHeaderNoBorder}>
              <Text style={mergedStyles.sectionTitle}>{customSection.title}</Text>
            </View>
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={mergedStyles.certificationItem}>
                {item.content && (
                  <View style={mergedStyles.descriptionContainer}>
                    {renderBulletPoints(item.content, mergedStyles)}
                  </View>
                )}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {})
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Executive Header - Always stays at top */}
        <View style={mergedStyles.headerContainer}>
          <View style={mergedStyles.headerContent}>
            <View style={mergedStyles.nameSection}>
              <Text style={mergedStyles.name}>
                {personalInfo.firstName} {personalInfo.lastName}
              </Text>
              {professionalTitle && (
                <Text style={mergedStyles.subtitle}>{professionalTitle}</Text>
              )}
            </View>
            <View style={mergedStyles.contactGrid}>
              {personalInfo.email && (
                <View style={mergedStyles.contactRow}>
                  <Text style={mergedStyles.contactText}>{personalInfo.email}</Text>
                </View>
              )}
              {personalInfo.phone && (
                <View style={mergedStyles.contactRow}>
                  <Text style={mergedStyles.contactText}>{formatPhoneNumber(personalInfo.phone)}</Text>
                </View>
              )}
              {personalInfo.location && (
                <View style={mergedStyles.contactRow}>
                  <Text style={mergedStyles.contactText}>{personalInfo.location}</Text>
                </View>
              )}
              {personalInfo.linkedinUrl && (
                <View style={mergedStyles.contactRow}>
                  <Text style={mergedStyles.contactText}>
                    LinkedIn: {personalInfo.linkedinUrl.replace(/^https?:\/\//, '')}
                  </Text>
                </View>
              )}
              {personalInfo.githubUrl && (
                <View style={mergedStyles.contactRow}>
                  <Text style={mergedStyles.contactText}>
                    GitHub: {personalInfo.githubUrl.replace(/^https?:\/\//, '')}
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Render sections in custom order */}
        {finalSectionOrder.map((sectionId, index) => {
          const SectionComponent = sectionComponents[sectionId];
          return SectionComponent ? (
            <View key={`${sectionId}-${index}`}>
              {SectionComponent()}
            </View>
          ) : null;
        })}

      </Page>
    </Document>
  );
};

export default ExecutiveTemplate;
